import { Link } from "react-router-dom";
import { Sparkle, ShieldCheck, ChartLineUp, Snowflake, Leaf, Timer, ForkKnife, Cube, MagicWand, HeartStraight, Fire, ArrowRight } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";

const HERO_IMG = "https://images.unsplash.com/photo-1639405069836-f82aa6dcb900?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzbWFydCUyMHJlZnJpZ2VyYXRvciUyMGtpdGNoZW58ZW58MHx8fHwxNzgyNjg3NTI2fDA&ixlib=rb-4.1.0&q=85";
const INGREDIENTS_IMG = "https://images.pexels.com/photos/5208242/pexels-photo-5208242.jpeg";
const HYGIENE_IMG = "https://images.pexels.com/photos/13428093/pexels-photo-13428093.jpeg";

export default function Landing() {
    return (
        <div className="min-h-screen bg-white text-zinc-950" data-testid="landing-page">
            <Navbar />

            {/* Hero */}
            <section className="relative border-b border-zinc-200 overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
                <div className="mx-auto max-w-7xl px-6 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-end relative">
                    <div className="md:col-span-7 animate-fadein">
                        <div className="flex items-center gap-3 uppercase-tag mb-6">
                            <span className="w-8 h-px bg-zinc-400" />
                            <span>v1 · monfrigozero.app</span>
                            <span className="inline-flex items-center gap-1 text-fzblue"><Sparkle weight="fill" size={12} /> IA intégrée</span>
                        </div>
                        <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
                            L'IA qui <span className="text-fzblue">divise par 2</span><br />
                            votre gaspillage.<br />
                            <span className="text-zinc-500">Et protège votre santé.</span>
                        </h1>
                        <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
                            FrigoZero scanne votre frigo, calcule les DLC, génère des recettes intelligentes
                            et vous alerte sur l'hygiène alimentaire. Moins de déchets, plus de santé, plus d'argent économisé.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <Link data-testid="hero-cta-start" to="/register" className="btn-primary">
                                Créer mon frigo gratuit <ArrowRight size={16} />
                            </Link>
                            <Link data-testid="hero-cta-pricing" to="/pricing" className="btn-outline">Voir Premium</Link>
                        </div>
                        <div className="mt-10 grid grid-cols-3 max-w-lg gap-6">
                            <Stat n="-52%" label="Gaspillage foyer" />
                            <Stat n="+380€" label="Économies / an" />
                            <Stat n="12min" label="Temps recette IA" />
                        </div>
                    </div>
                    <div className="md:col-span-5 relative">
                        <div className="relative border border-zinc-200 bg-white p-3">
                            <img src={HERO_IMG} alt="Frigo intelligent" className="w-full h-[480px] object-cover" />
                            <div className="absolute -bottom-5 -left-5 bg-white border border-zinc-200 p-4 w-64">
                                <div className="uppercase-tag">Analyse IA</div>
                                <div className="mt-2 font-display font-bold text-lg">3 aliments à sauver</div>
                                <div className="mt-2 flex items-center gap-2 text-fzgreen text-sm">
                                    <ShieldCheck weight="fill" /> Score hygiène 9.4 / 10
                                </div>
                            </div>
                            <div className="absolute -top-5 -right-5 bg-fzblue text-white px-4 py-3 font-mono text-xs tracking-wider">
                                LIVE · SCAN FRIGO
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust / marquee */}
            <section className="border-b border-zinc-200 py-6 overflow-hidden bg-zinc-50">
                <div className="marquee-track uppercase-tag gap-16 whitespace-nowrap">
                    {["a", "b"].map((track) => (
                        <div key={`marquee-${track}`} className="flex gap-16 pr-16">
                            <span>✱ Anti-gaspillage IA</span><span>✱ DLC intelligentes</span>
                            <span>✱ Recettes santé personnalisées</span><span>✱ Impact CO₂ mesuré</span>
                            <span>✱ Alertes hygiène temps réel</span><span>✱ Made in France</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features (Tetris grid) */}
            <section id="features" className="border-b border-zinc-200">
                <div className="mx-auto max-w-7xl px-6 py-24">
                    <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
                        <div className="md:col-span-7">
                            <div className="uppercase-tag mb-4">01 · Fonctionnalités</div>
                            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                                Une révolution technologique<br />au service du réel.
                            </h2>
                        </div>
                        <p className="md:col-span-5 text-zinc-600">
                            Une stack IA dernière génération (GPT-5.2) analyse en continu vos ingrédients,
                            leur fraîcheur, votre profil santé et vos habitudes pour optimiser chaque repas.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-12 gap-4">
                        <Feature className="md:col-span-5" icon={<MagicWand size={28} weight="duotone" />} tag="IA GPT-5.2"
                            title="Recettes générées en temps réel"
                            body="L'IA priorise les aliments qui expirent, propose 3 recettes santé personnalisées en <15s, avec astuces d'hygiène." />
                        <Feature className="md:col-span-7" icon={<Snowflake size={28} weight="duotone" />} tag="Inventaire"
                            title="Frigo · Congélateur · Placard"
                            body="Ajoutez vos aliments en 1 clic. FrigoZero calcule automatiquement les DLC, trie par urgence et vous alerte." large />
                        <Feature className="md:col-span-7" icon={<Cube size={28} weight="duotone" />} tag="Vision IA · NOUVEAU"
                            title="Scanner mon frigo par photo"
                            body="Ouvrez la caméra, prenez une photo (ou plusieurs). L'IA reconnaît chaque aliment, estime la fraîcheur et remplit votre frigo automatiquement." large />
                        <Feature className="md:col-span-7" icon={<ShieldCheck size={28} weight="duotone" />} tag="Santé"
                            title="Analyse d'hygiène automatisée"
                            body="Chaque aliment reçoit un score de fraîcheur et un verdict santé signé par l'IA experte en sécurité alimentaire." large />
                        <Feature className="md:col-span-5" icon={<ChartLineUp size={28} weight="duotone" />} tag="Impact"
                            title="Statistiques mesurées"
                            body="Kg sauvés, euros économisés, CO₂ évité. Un tableau de bord d'impact qui motive au quotidien." />
                        <Feature className="md:col-span-4" icon={<Timer size={28} weight="duotone" />} tag="Alertes"
                            title="Notifications DLC"
                            body="Recevez les bonnes alertes au bon moment. Fini les 'oh non, c'est périmé'." />
                        <Feature className="md:col-span-4" icon={<HeartStraight size={28} weight="duotone" />} tag="Bien-être"
                            title="Profil santé"
                            body="Régimes, allergies, objectifs : l'IA adapte chaque suggestion." />
                        <Feature className="md:col-span-4" icon={<Leaf size={28} weight="duotone" />} tag="Écologie"
                            title="Impact carbone réel"
                            body="Chaque geste compte. Mesurez votre contribution planète en direct." />
                    </div>
                </div>
            </section>

            {/* AI Section */}
            <section id="ai" className="border-b border-zinc-200 bg-zinc-950 text-white">
                <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <div className="uppercase-tag mb-4 text-zinc-400">02 · Intelligence</div>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                            Un chef IA dans<br />chaque frigo.
                        </h2>
                        <p className="mt-6 text-zinc-300 max-w-lg">
                            Propulsé par GPT-5.2 et un moteur d'analyse nutritionnelle propriétaire.
                            L'IA lit vos ingrédients, comprend leurs DLC, croise avec votre profil santé
                            et génère des recettes en streaming — en français, en 3 secondes.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                            <MiniStat val="GPT-5.2" label="Moteur IA" />
                            <MiniStat val="< 3s" label="Latence recette" />
                            <MiniStat val="98.4%" label="Précision DLC" />
                            <MiniStat val="24/7" label="Analyse continue" />
                        </div>
                    </div>
                    <div className="md:col-span-6 relative">
                        <div className="border border-zinc-800 bg-zinc-900 p-6 font-mono text-xs">
                            <div className="flex items-center gap-2 text-zinc-500 text-[10px]">
                                <span className="w-2 h-2 bg-fzgreen rounded-full" /> FRIGOZERO · SESSION LIVE
                            </div>
                            <pre className="mt-4 whitespace-pre-wrap text-zinc-200 leading-relaxed">
{`> analyse frigo #1
✱ 12 ingrédients détectés
✱ 3 urgents (expirent < 48h)
  - yaourts nature x4
  - épinards frais x1
  - filet de poulet x1

> génération recette santé...
### Poulet aux épinards & yaourt
- 25 min · Difficulté 2/5
- Score santé 9.1/10
- Impact anti-gaspi : 3 aliments sauvés
- Hygiène : cuire à cœur 74°C`}
                            </pre>
                            <div className="mt-4 flex items-center gap-2 text-fzgreen">
                                <span className="w-1.5 h-1.5 bg-fzgreen rounded-full animate-pulse" />
                                <span>IA opérationnelle</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Health & Hygiene */}
            <section id="health" className="border-b border-zinc-200">
                <div className="mx-auto max-w-7xl px-6 py-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5 relative">
                        <img src={HYGIENE_IMG} alt="Hygiène alimentaire" className="w-full h-[420px] object-cover border border-zinc-200" />
                        <div className="absolute -bottom-6 -right-6 bg-white border border-zinc-200 p-5 max-w-xs">
                            <div className="uppercase-tag">Score hygiène</div>
                            <div className="mt-2 font-display font-black text-4xl text-fzgreen">9.4</div>
                            <div className="text-sm text-zinc-500">Aliments dans les bonnes conditions</div>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <div className="uppercase-tag mb-4">03 · Santé & Hygiène</div>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                            La sécurité alimentaire<br />comme une évidence.
                        </h2>
                        <p className="mt-6 text-zinc-600 max-w-xl">
                            FrigoZero applique les meilleures pratiques HACCP directement dans votre poche.
                            Températures, DLC, contamination croisée : chaque aliment est analysé.
                        </p>
                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            <HealthCard title="Conservation optimale" body="Instructions précises pour chaque aliment (frigo, congélateur, placard)." />
                            <HealthCard title="Signes d'altération" body="L'IA reconnaît et vous alerte des signaux à vérifier avant consommation." />
                            <HealthCard title="Verdict santé" body="Feu vert, orange ou rouge : décidez en 2 secondes." />
                            <HealthCard title="Traçabilité personnelle" body="Historique complet de tout ce qui est passé par votre frigo." />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-b border-zinc-200 relative overflow-hidden">
                <div className="absolute inset-0 stripe" />
                <div className="mx-auto max-w-7xl px-6 py-24 relative grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <div className="uppercase-tag mb-4">04 · Rejoignez</div>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                            Prêt à zéro gaspillage ?
                        </h2>
                        <p className="mt-4 text-zinc-600 max-w-xl">
                            Gratuit à vie pour les 500 premiers utilisateurs. Installable comme app mobile.
                        </p>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end gap-4">
                        <Link data-testid="cta-register-bottom" to="/register" className="btn-primary">
                            Créer mon compte <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="mx-auto max-w-7xl px-6 py-14 grid md:grid-cols-12 gap-8 text-sm">
                <div className="md:col-span-5">
                    <Logo />
                    <p className="mt-4 text-zinc-500 max-w-xs">
                        L'application anti-gaspillage IA. Fait avec ♥ en France. monfrigozero.app
                    </p>
                </div>
                <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-6 text-zinc-600">
                    <div>
                        <div className="uppercase-tag mb-2">Produit</div>
                        <div>Fonctionnalités</div>
                        <div>Tarifs</div>
                        <div>Roadmap</div>
                    </div>
                    <div>
                        <div className="uppercase-tag mb-2">Société</div>
                        <div>À propos</div>
                        <div>Blog</div>
                        <div>Presse</div>
                    </div>
                    <div>
                        <div className="uppercase-tag mb-2">Légal</div>
                        <div>CGU</div>
                        <div>Confidentialité</div>
                        <div>Cookies</div>
                    </div>
                    <div>
                        <div className="uppercase-tag mb-2">Contact</div>
                        <div>hello@monfrigozero.app</div>
                        <div>Instagram</div>
                        <div>TikTok</div>
                    </div>
                </div>
            </footer>
            <div className="border-t border-zinc-200 py-4 text-center text-xs text-zinc-400">
                © 2026 FrigoZero · monfrigozero.app · Tous droits réservés
            </div>
        </div>
    );
}

function Stat({ n, label }) {
    return (
        <div>
            <div className="font-display font-black text-3xl tracking-tighter">{n}</div>
            <div className="uppercase-tag mt-1">{label}</div>
        </div>
    );
}

function MiniStat({ val, label }) {
    return (
        <div className="border border-zinc-800 p-4">
            <div className="font-display font-black text-2xl">{val}</div>
            <div className="uppercase-tag mt-1 text-zinc-400">{label}</div>
        </div>
    );
}

function Feature({ icon, tag, title, body, className = "", large = false }) {
    return (
        <div className={"card-flat p-6 md:p-8 hover:-translate-y-1 transition-transform " + className}>
            <div className="text-fzblue mb-6">{icon}</div>
            <div className="uppercase-tag">{tag}</div>
            <div className={"font-display font-bold mt-2 " + (large ? "text-2xl md:text-3xl" : "text-xl")}>{title}</div>
            <p className="mt-3 text-zinc-600 text-sm leading-relaxed">{body}</p>
        </div>
    );
}

function HealthCard({ title, body }) {
    return (
        <div className="border-l-2 border-fzblue pl-4 py-2">
            <div className="font-display font-semibold">{title}</div>
            <div className="text-sm text-zinc-600 mt-1">{body}</div>
        </div>
    );
}

function Logo() {
    return (
        <div className="flex items-center gap-2">
            <svg width={24} height={24} viewBox="0 0 32 32" fill="none">
                <rect x="4" y="3" width="24" height="26" rx="2" fill="#09090B"/>
                <rect x="6" y="5" width="20" height="9" rx="1" fill="#FFFFFF"/>
                <rect x="6" y="16" width="20" height="11" rx="1" fill="#FFFFFF"/>
                <circle cx="23.5" cy="9.5" r="1.5" fill="#002FA7"/>
                <circle cx="23.5" cy="21.5" r="1.5" fill="#00C853"/>
            </svg>
            <div className="font-display font-black tracking-tighter text-lg">
                <span>FRIGO</span><span className="text-fzblue">ZERO.</span>
            </div>
        </div>
    );
}
